// A text block's AI summary: starts one (POST …/text_summary) and polls the block until the worker
// has written it. Sending no selection regenerates from the one the block saved.
import { useContext, useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { apiGet, apiPost } from 'api'
import AuthContext from 'context/auth-context'

const POLL_MS = 2500

export const startTextSummary = ({ token, adapter, chartId, selection }) =>
  apiPost({ url: adapter.summaryPath(chartId), token, objectName: 'text_summary', params: selection || {} })

export function useTextSummary({ adapter, containerId, chartId, summaryConfig, onFinished }) {
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [latest, setLatest] = useState(summaryConfig || null)

  const savedJson = JSON.stringify(summaryConfig || null)
  useEffect(() => setLatest(JSON.parse(savedJson)), [savedJson])

  const isGenerating = latest?.status === 'generating'

  const { mutate, isLoading: isStarting, error: startError } = useMutation(
    (selection) => startTextSummary({ token, adapter, chartId, selection }),
    {
      onSuccess: (chart) => {
        setLatest(chart?.params?.summary_config || null)
        adapter.invalidate(queryClient, containerId)
      }
    }
  )

  useQuery(['text-summary', chartId], () => apiGet({ url: adapter.chartPath(chartId), token }), {
    enabled: !!chartId && isGenerating,
    refetchInterval: POLL_MS,
    cacheTime: 0,
    onSuccess: (chart) => {
      const config = chart?.params?.summary_config || null
      if (config?.status === 'generating') return

      setLatest(config)
      adapter.invalidate(queryClient, containerId)
      onFinished?.(chart)
    }
  })

  return {
    generate: (selection) => chartId && mutate(selection),
    config: latest,
    isGenerating: isStarting || isGenerating,
    startError: startError?.response?.data?.message || startError?.message || null
  }
}
